import React, { useRef, useEffect, useState } from 'react';
import { Button } from '../../../../ui/button';
import { ChevronRightIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Sparkles } from 'lucide-react';
import ChatMessage from './ChatMessage';
import ThinkingBlock from './ThinkingBlock';
import AgentActivityBlock from './AgentActivityBlock';
import { ChatMessage as ChatMessageType } from '../../../../../types/case-workspace';
import { ToolCall, AgentProgressEvent } from '../../../../../hooks/useSimulationStream';

interface ChatPanelProps {
  messages: ChatMessageType[];
  inputMessage: string;
  isTyping: boolean;
  suggestedQuestions: string[];
  currentStatus?: string | null;
  progressMessage?: string | null;
  activatedSkills?: string[] | null;
  toolCalls?: ToolCall[];
  agentProgress?: AgentProgressEvent[];
  isSimulationComplete?: boolean;
  onInputChange: (message: string) => void;
  onSendMessage: (message: string) => void;
  isOpen: boolean;
  onClose: () => void;
  onDecisionClick?: (decisionIdOrReference: string) => void;
  showHeader?: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  inputMessage,
  isTyping,
  suggestedQuestions,
  currentStatus,
  progressMessage,
  activatedSkills,
  toolCalls = [],
  agentProgress = [],
  isSimulationComplete = false,
  onInputChange,
  onSendMessage,
  isOpen,
  onClose,
  onDecisionClick,
  showHeader = true,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showSuggestedQuestions, setShowSuggestedQuestions] = useState(true);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const lastMessageCountRef = useRef(0);

  // Reset suggested questions visibility when new questions arrive
  useEffect(() => {
    if (suggestedQuestions.length > 0) {
      setShowSuggestedQuestions(true);
    }
  }, [suggestedQuestions]);

  // Track user scroll behavior
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const threshold = 50; // px from bottom
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;

    setUserHasScrolledUp(!isAtBottom);
  };

  // Auto-scroll to bottom when new messages arrive (only if user hasn't scrolled up)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Always scroll on initial mount or when chat is opened
    if (messages.length > 0 && lastMessageCountRef.current === 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      lastMessageCountRef.current = messages.length;
      setUserHasScrolledUp(false);
      return;
    }

    // Always scroll when sending a new message (message count increased)
    const hasNewMessage = messages.length > lastMessageCountRef.current;
    if (hasNewMessage) {
      lastMessageCountRef.current = messages.length;

      // If user hasn't manually scrolled up, auto-scroll
      if (!userHasScrolledUp) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }

    // Also scroll when typing indicator appears/disappears
    if (!userHasScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, userHasScrolledUp]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputMessage.trim()) {
        onSendMessage(inputMessage);
      }
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    onSendMessage(question);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-card">
      {showHeader && (
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Ask Juno</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 transition-colors hover:bg-muted"
            aria-label="Close chat panel"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Thinking Block - Only shown when there's a status or progress message */}
      {(currentStatus || progressMessage) && (
        <div className="border-b border-border animate-in fade-in duration-300 flex-shrink-0">
          <ThinkingBlock status={currentStatus} progressMessage={progressMessage} activatedSkills={activatedSkills} />
        </div>
      )}

      {/* Agent Activity Block - Shown after simulation completes */}
      {showHeader && (
        <AgentActivityBlock
          activatedSkills={activatedSkills}
          toolCalls={toolCalls}
          agentProgress={agentProgress}
          isComplete={isSimulationComplete}
        />
      )}

      {/* Message Thread (scrollable) */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
      >
        {messages.length === 0 && !currentStatus && !progressMessage ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--go-good-vibes-tint)]">
              <Sparkles className="h-6 w-6 text-primary/70" />
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Ask Juno about this simulation
            </p>
            <p className="text-xs text-muted-foreground">
              Get insights on reasoning, evidence, and precedents
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} onDecisionClick={onDecisionClick} />
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div className="rounded-lg bg-muted p-3">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary/50" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary/50" style={{ animationDelay: '0.1s' }} />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary/50" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Auto-scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Questions - Dismissible */}
      {suggestedQuestions.length > 0 && showSuggestedQuestions && (
        <div className="border-t border-border flex-shrink-0">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-foreground">Suggested questions:</p>
              <button
                onClick={() => setShowSuggestedQuestions(false)}
                className="rounded p-1 transition-colors hover:bg-muted"
                aria-label="Dismiss suggested questions"
              >
                <XMarkIcon className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-2">
              {suggestedQuestions.map((question, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestedQuestion(question)}
                  className="w-full rounded-lg border border-transparent bg-[var(--go-good-vibes-tint)] px-4 py-3 text-left text-sm transition-colors hover:border-primary/15 hover:bg-[var(--go-good-vibes-tint-hover)]"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="p-4 border-t border-border flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={inputMessage}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Juno about this simulation..."
            className="max-h-32 flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            rows={2}
          />
          <Button
            onClick={() => onSendMessage(inputMessage)}
            disabled={!inputMessage.trim() || isTyping}
            size="sm"
            className="flex-shrink-0"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
